import { FormControlLabel, Switch, styled } from '@mui/material'
import React from 'react'

import { SearchableSelect } from '@/app/component/atom/form'
import PzInput from '@/app/component/atom/pz-input'
import { PersonalizationType, SelectOption } from '@/app/types'

type GroupedOption = {
  title: string
  data: SelectOption<string>[]
}

type Props = {
  error?: boolean
  field: string
  fieldError?: boolean
  helperText?: string
  hideRemoveSelection?: boolean
  id?: string
  label?: string
  name?: string
  options: GroupedOption[]
  personalizationList: PersonalizationType
  required?: boolean

  onBlur?: () => void
  onChange: (field: string) => void
  onSelect: (field: string) => void
}

const ConditionField: React.FC<Props> = ({
  error,
  field,
  fieldError,
  helperText = 'You must select a value',
  hideRemoveSelection = true,
  id = 'conditions-field-select',
  label,
  name = 'condition-field',
  options,
  personalizationList,
  required,
  onBlur,
  onChange,
  onSelect,
}) => {
  const [advancedChoice, setAdvanced] = React.useState<boolean>()
  const advanced = advancedChoice ?? isAdvanced(field, options)

  const renderInput = () => {
    if (advanced) {
      return (
        <PzInput
          error={fieldError ? helperText : ''}
          label={label ?? 'Enter value'}
          name={`${name}-advanced`}
          personalizationList={personalizationList}
          required={required}
          value={field}
          onBlur={onBlur}
          onChange={(value) => {
            setAdvanced(true)
            onChange(value)
          }}
        />
      )
    }

    return (
      <SearchableSelect
        id={id}
        error={!field && error}
        formControlProps={selectFormControlProps}
        grouped
        helperText={fieldError ? helperText : ''}
        hideRemoveSelection={hideRemoveSelection}
        label={label ?? (field ? ' ' : 'Select field')}
        options={options}
        required={required}
        showAll
        value={field}
        keyPropFn={keyPropFn}
        onBlur={onBlur}
        onChange={onSelect}
        valuePropFn={valuePropFn}
      />
    )
  }

  const handleToggle = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { checked } = e.target
      if (field && !checked && isAdvanced(field, options)) {
        // if advanced is getting disabled and the field is not a single personalization, reset field
        onChange('')
      }
      setAdvanced(checked)
    },
    [field, options, onChange],
  )

  return (
    <Container>
      {renderInput()}
      <FormControlLabel
        control={<Switch checked={!!advanced} onChange={handleToggle} />}
        label="Advanced"
        name={`${name}-advanced-toggle`}
        sx={{
          width: 'fit-content',
        }}
      />
    </Container>
  )
}

const Container = styled('div')(() => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
}))

const selectFormControlProps = {
  margin: 'none',
  style: {
    width: '100%',
  },
}

const isAdvanced = (field: string, options: GroupedOption[]) => {
  if (!field) {
    return false
  }
  return !options.some((group) => group.data.some((option) => option.value === field))
}
const keyPropFn = (option: SelectOption<string>) => option.value
const valuePropFn = (option: SelectOption<string>) => option.label

export default React.memo(ConditionField)
